import {
  iconArrowRotateLeft,
  iconBox,
  iconCircleQuestion,
  iconHandshake,
  iconMagnifyingGlass,
  iconPen,
  iconQuoteLeft,
  type IconDefinition,
} from '@/lib/icons';
import type {
  EngageCluContent,
  EngageCluUseOption,
  EngageStep,
  EngageStepBrief,
} from '@/store/features/engage.feature';
import { pathTo } from '../sequence-tree.util';
import { MAX_WRITING_SKILLS } from './clu-writing.util';

/** A prebuilt writing skill's key, as the API names it. */
export type BuiltinWritingSkillKey =
  | 'product'
  | 'proof'
  | 'crm'
  | 'research'
  | 'pain_question'
  | 'close_the_loop';

export interface BuiltinWritingSkill {
  key: BuiltinWritingSkillKey;
  name: string;
  icon: IconDefinition;
}

/** On for every Clu email, so it has no key. */
export const COMPANY_VOICE: { name: string; icon: IconDefinition } = {
  name: 'Company voice',
  icon: iconPen,
};

/** The prebuilt writing skills, in the API's order (`clu_setup.BUILTIN_SKILLS`). */
export const BUILTIN_WRITING_SKILLS: readonly BuiltinWritingSkill[] = [
  { key: 'product', name: 'Product and pitch', icon: iconBox },
  { key: 'proof', name: 'Customer proof', icon: iconQuoteLeft },
  { key: 'crm', name: 'CRM history', icon: iconHandshake },
  { key: 'research', name: 'Research', icon: iconMagnifyingGlass },
  { key: 'pain_question', name: 'Pain question', icon: iconCircleQuestion },
  { key: 'close_the_loop', name: 'Close the loop', icon: iconArrowRotateLeft },
];

/** The prebuilt skills a Clu step adds from Add skill. */
export const ADDED_SKILLS: readonly BuiltinWritingSkillKey[] = [
  'pain_question',
  'close_the_loop',
];

const ORDER = BUILTIN_WRITING_SKILLS.map((skill) => skill.key);

export const isBuiltinSkill = (
  entry: string
): entry is BuiltinWritingSkillKey =>
  (ORDER as readonly string[]).includes(entry);

export const builtinSkill = (
  key: BuiltinWritingSkillKey
): BuiltinWritingSkill =>
  BUILTIN_WRITING_SKILLS.find((skill) => skill.key === key)!;

/**
 * Skills in the API's order: the prebuilt keys in theirs, then the team's
 * skill ids as given (their instruction order), each once. Blanks and
 * anything that isn't a string are dropped.
 */
export function normalizedSkills(entries: readonly unknown[]): string[] {
  const unique = entries.filter(
    (entry, index): entry is string =>
      typeof entry === 'string' &&
      entry.trim() !== '' &&
      entries.indexOf(entry) === index
  );
  return [
    ...ORDER.filter((key) => unique.includes(key)),
    ...unique.filter((entry) => !isBuiltinSkill(entry)),
  ];
}

/** The team's skill ids among a step's skills, in order. */
export const teamSkillIds = (skills: readonly string[]): string[] =>
  skills.filter((entry) => !isBuiltinSkill(entry));

/** The skill each old Use option became. */
const USE_SKILL: Record<EngageCluUseOption, BuiltinWritingSkillKey> = {
  product_feature: 'product',
  social_proof: 'proof',
  case_study: 'proof',
  their_news: 'research',
  pain_question: 'pain_question',
  close_the_loop: 'close_the_loop',
};

/**
 * A brief's skills. A brief with no `skills` (an API that doesn't send them)
 * reads its old Use options and legacy research as the skills they became.
 */
export function briefSkills(
  brief: Pick<EngageStepBrief, 'skills' | 'use' | 'research'> | null | undefined
): string[] {
  if (!brief) return [];
  if (Array.isArray(brief.skills)) return normalizedSkills(brief.skills);
  const use = Array.isArray(brief.use) ? brief.use : [];
  const keys: string[] = use.map((option) => USE_SKILL[option]);
  if (brief.research === true) keys.push('research');
  return normalizedSkills(keys);
}

/**
 * The skills with one more. A repeat, or a team skill past the 3 a step
 * takes, leaves them unchanged.
 */
export function addSkill(skills: readonly string[], entry: string): string[] {
  if (skills.includes(entry)) return [...skills];
  if (
    !isBuiltinSkill(entry) &&
    teamSkillIds(skills).length >= MAX_WRITING_SKILLS
  )
    return [...skills];
  return normalizedSkills([...skills, entry]);
}

export const removeSkill = (skills: readonly string[], entry: string) =>
  skills.filter((item) => item !== entry);

/** The skills with a team skill moved a place up (-1) or down (1). */
export function moveSkill(
  skills: readonly string[],
  id: string,
  by: -1 | 1
): string[] {
  const team = teamSkillIds(skills);
  const from = team.indexOf(id);
  const to = from + by;
  if (from < 0 || to < 0 || to >= team.length) return [...skills];
  [team[from], team[to]] = [team[to], team[from]];
  return [...skills.filter(isBuiltinSkill), ...team];
}

/** Nothing for Customer proof to cite. Counts not read yet aren't missing. */
export const proofMissing = (content: EngageCluContent | null | undefined) =>
  content != null && content.customer_proof === 0 && content.case_studies === 0;

/**
 * The skills of the nearest Clu email above `step` on its path (its own
 * column and the columns above it, never the other arm). Null with none.
 */
export function cluSkillsAbove(
  steps: readonly EngageStep[],
  step: Pick<EngageStep, 'id'>
): string[] | null {
  const self = steps.find((item) => item.id === step.id);
  if (!self) return null;
  const above = pathTo(steps, self).filter(
    (item) => item.kind === 'email' && item.writer === 'clu'
  );
  const nearest = above[above.length - 1];
  return nearest ? briefSkills(nearest.brief) : null;
}

/**
 * The skills a new Clu step starts with: those of the Clu step above it,
 * or with none, Product and pitch, plus Customer proof when there is proof
 * or a case study and CRM history when a CRM is connected. A team skill the
 * library no longer has is left behind (`live`: the library's ids, unset
 * while not known, as before it is read or after a failed read), so the
 * switch isn't refused.
 */
export function startingSkills({
  above,
  content,
  crmConnected,
  live,
}: {
  above: readonly string[] | null;
  content: EngageCluContent | null | undefined;
  crmConnected: boolean;
  live?: readonly string[];
}): string[] {
  if (above)
    return normalizedSkills(
      above.filter(
        (entry) => isBuiltinSkill(entry) || !live || live.includes(entry)
      )
    );
  const skills: string[] = ['product'];
  if (content && (content.customer_proof > 0 || content.case_studies > 0))
    skills.push('proof');
  if (crmConnected) skills.push('crm');
  return skills;
}
